/* eslint-disable eag/match-named-export */
import {CSSProperties} from 'react';

import {Breakpoint} from '../types/Breakpoint';
import {Integer} from '../types/Integer';
import {ResponsiveValue, isResponsiveValue} from '../types/ResponsiveValue';

/**
 * Converts spacing value to rem string
 * Each unit = 0.25rem (matching old foundation's --eag-space-step * n and Tailwind's spacing scale)
 */
export const toSpacingValue = (value: number): string => `${value * 0.25}rem`;

/**
 * Gets inline styles for spacing props (padding, gap)
 * Always uses inline styles for simplicity and full flexibility
 *
 * @param value - Simple Integer or ResponsiveValue<Integer>
 * @param cssProperty - CSS property name (e.g., 'padding', 'paddingTop', 'gap')
 * @returns CSSProperties object with the spacing value
 *
 * @example
 * getSpacingStyles(4, 'padding')
 * // → { padding: '1rem' }
 *
 * @example
 * getSpacingStyles({ base: 2, lg: 8 }, 'padding')
 * // → { padding: '0.5rem' } // Note: responsive values use base value only for inline styles
 */
export const getSpacingStyles = (
  value: Integer | ResponsiveValue<Integer> | undefined,
  cssProperty: keyof CSSProperties
): CSSProperties => {
  if (value === undefined) {
    return {};
  }

  // For responsive values, use base value (inline styles can't be responsive)
  // Components should use CSS variables or media queries for true responsive spacing
  if (isResponsiveValue(value)) {
    const baseValue = value.base;
    if (baseValue === undefined) {
      return {};
    }
    return {[cssProperty]: toSpacingValue(baseValue)};
  }

  return {[cssProperty]: toSpacingValue(value)};
};

/**
 * Gets class from a static map (for non-spacing values like radius, shadow, etc.)
 *
 * @param value - The value to look up
 * @param classMap - Map of values to Tailwind classes
 * @returns Tailwind class or undefined
 */
export const getStaticClass = <T extends string>(
  value: T | undefined,
  classMap: Record<T, string>
): string | undefined => {
  if (value === undefined) {
    return undefined;
  }
  return classMap[value];
};

/**
 * Gets responsive classes from a static map
 * Handles both simple values and responsive value objects
 */
export const getResponsiveStaticClass = <T extends string>(
  value: T | ResponsiveValue<T> | undefined,
  classMap: Record<T, string>
): string | undefined => {
  if (value === undefined) {
    return undefined;
  }

  if (!isResponsiveValue(value)) {
    return classMap[value];
  }

  const breakpointPrefixes: Record<Breakpoint, string> = {
    base: '',
    sm: 'sm:',
    md: 'md:',
    lg: 'lg:',
  };

  const classes: string[] = [];
  const breakpoints: Breakpoint[] = ['base', 'sm', 'md', 'lg'];

  for (const breakpoint of breakpoints) {
    const breakpointValue = value[breakpoint];
    if (breakpointValue === undefined) {
      continue;
    }

    const twClass = classMap[breakpointValue];
    if (twClass) {
      const prefix = breakpointPrefixes[breakpoint];
      classes.push(`${prefix}${twClass}`);
    }
  }

  return classes.length > 0 ? classes.join(' ') : undefined;
};
